/* Xiaomi 2026 · Privilege × Tier Utilization Matrix
 * (Dhiraj-inspired CEO landing heat-map)
 *
 * Six-state selector pill (Eligible / Approval Pending / Approved /
 * Gift Sent / Availed / Expired / All) drives the colour intensity of
 * each cell. Click any cell → drill-down dealer list.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHdr = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const STATUSES = [
  { id: 'all',              label: 'All open',         tone: '#15110d' },
  { id: 'eligible',         label: 'Eligible',         tone: '#6b7280' },
  { id: 'approval_pending', label: 'Approval pending', tone: '#f59e0b' },
  { id: 'approved',         label: 'Approved',         tone: '#3b82f6' },
  { id: 'gift_sent',        label: 'Gift sent',        tone: '#8b5cf6' },
  { id: 'availed',          label: 'Availed',          tone: '#16a34a' },
  { id: 'expired',          label: 'Expired',          tone: '#dc2626' },
];

function shade(count, max) {
  if (count == null) return '#f3f4f6';
  if (!count) return '#fff';
  const intensity = Math.min(1, count / Math.max(1, max));
  // light orange → deep orange
  const a = Math.round(intensity * 100) / 100;
  return `rgba(255, 105, 0, ${0.15 + a * 0.65})`;
}

export default function XiaomiMatrix() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('all');
  const [drill, setDrill] = useState(null);

  const load = (s) => {
    axios
      .get(`${API}/xiaomi/matrix?status=${s}`, authHdr())
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Failed to load matrix'));
  };

  useEffect(() => {
    load(status);
  }, [status]);

  const onCellClick = async (tier, benefit_id) => {
    try {
      const r = await axios.get(
        `${API}/xiaomi/matrix/drill?tier=${encodeURIComponent(tier)}&benefit_id=${benefit_id}&status=${status}`,
        authHdr(),
      );
      setDrill(r.data);
    } catch (e) {
      toast.error('Drill failed');
    }
  };

  if (!data) {
    return <div className="text-sm text-soft p-8">Loading matrix…</div>;
  }

  const maxCount = Math.max(
    1,
    ...(data.matrix || []).flatMap((row) => (row.cells || []).map((c) => c.count || 0)),
  );

  return (
    <div className="space-y-6" data-testid="xiaomi-matrix-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Fraunces, serif', color: '#15110d' }}>
            Privilege × Tier <span style={{ color: '#ff6900' }}>Utilization</span>
          </h1>
          <p className="text-sm text-soft mt-1">
            {data.total_dealers} partners across 4 tiers · {data.matrix.length} benefits in the 2026 catalogue.
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="matrix-status-pills">
          {STATUSES.map((s) => (
            <button
              key={s.id}
              onClick={() => setStatus(s.id)}
              className="px-3 py-1.5 rounded-full text-[11px] font-semibold border transition"
              style={{
                borderColor: status === s.id ? s.tone : '#e6dccd',
                background: status === s.id ? s.tone : '#fff',
                color: status === s.id ? '#fff' : s.tone,
              }}
              data-testid={`matrix-pill-${s.id}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Funnel summary */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="matrix-funnel">
        {['eligible', 'approval_pending', 'approved', 'gift_sent', 'availed', 'expired'].map((k) => (
          <button
            key={k}
            onClick={() => setStatus(k)}
            className="bg-white border rounded-xl px-4 py-3 text-left hover:shadow-md transition"
            style={{ borderColor: status === k ? '#ff6900' : '#e6dccd' }}
          >
            <div className="text-[10px] uppercase tracking-wider text-soft">
              {STATUSES.find((s) => s.id === k)?.label}
            </div>
            <div className="text-2xl font-bold" style={{ color: STATUSES.find((s) => s.id === k)?.tone, fontFamily: 'Fraunces, serif' }}>
              {data.funnel[k] ?? 0}
            </div>
          </button>
        ))}
      </div>

      {/* Matrix table */}
      <div className="overflow-x-auto border border-line rounded-xl bg-white" data-testid="matrix-table">
        <table className="w-full text-sm">
          <thead style={{ background: '#f3ebd9' }}>
            <tr>
              <th className="px-4 py-2.5 text-left text-[10px] uppercase tracking-wider text-soft">Benefit</th>
              {data.tiers.map((t) => (
                <th key={t.id} className="px-4 py-2.5 text-center text-[10px] uppercase tracking-wider text-soft">
                  {t.label}
                  <div className="text-[9px] font-normal opacity-70">{t.count} partners</div>
                </th>
              ))}
              <th className="px-4 py-2.5 text-center text-[10px] uppercase tracking-wider text-soft">Total</th>
            </tr>
          </thead>
          <tbody>
            {data.matrix.map((row) => (
              <tr key={row.benefit_id} className="border-t border-line">
                <td className="px-4 py-3 font-medium text-[#15110d]">{row.benefit_name}</td>
                {row.cells.map((c) => (
                  <td
                    key={c.tier}
                    className={`px-4 py-3 text-center ${c.eligible ? 'cursor-pointer hover:ring-2 hover:ring-[#ff6900]' : ''}`}
                    style={{
                      background: c.eligible ? shade(c.count, maxCount) : '#fafafa',
                      color: c.eligible ? '#15110d' : '#d1d5db',
                    }}
                    onClick={() => c.eligible && onCellClick(c.tier, row.benefit_id)}
                    title={c.eligible ? `${c.count} · click to drill in` : 'Not eligible'}
                    data-testid={`matrix-cell-${row.benefit_id}-${c.tier}`}
                  >
                    {c.eligible ? (
                      <div>
                        <div className="text-base font-bold">{c.count}</div>
                        {c.secondary && (
                          <div className="text-[9px] text-soft mt-0.5">{c.secondary}</div>
                        )}
                      </div>
                    ) : (
                      '—'
                    )}
                  </td>
                ))}
                <td className="px-4 py-3 text-center font-bold" style={{ background: '#fffdf9' }}>
                  {row.total}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Drill-in dealer list (modal) */}
      {drill && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setDrill(null)}
          data-testid="matrix-drill-modal"
        >
          <div
            className="bg-white rounded-lg max-w-3xl w-full max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b sticky top-0 bg-white flex justify-between items-start" style={{ borderColor: '#e6dccd' }}>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-soft">Drill in · {drill.status}</div>
                <h2 className="text-lg font-semibold" style={{ fontFamily: 'Fraunces, serif' }}>
                  {drill.tier} · {drill.benefit_id}
                </h2>
                <div className="text-xs text-soft">{drill.count} partners</div>
              </div>
              <button onClick={() => setDrill(null)} className="text-2xl text-soft hover:text-ink"
                data-testid="matrix-drill-close">×</button>
            </div>
            <div className="p-3">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-soft">
                    <th className="text-left px-2 py-1.5">Outlet</th>
                    <th className="text-left px-2 py-1.5">Owner</th>
                    <th className="text-left px-2 py-1.5">City</th>
                    <th className="text-left px-2 py-1.5">Status</th>
                    <th className="text-left px-2 py-1.5">Step</th>
                    <th className="text-left px-2 py-1.5">AWB</th>
                  </tr>
                </thead>
                <tbody>
                  {drill.rows.map((r) => (
                    <tr
                      key={r.retailer_id}
                      className="border-t border-line hover:bg-[#fffbf0] cursor-pointer"
                      onClick={() => { setDrill(null); navigate(`/xiaomi/partners/${r.retailer_id}`); }}
                    >
                      <td className="px-2 py-2 font-medium">{r.outlet_name}</td>
                      <td className="px-2 py-2">{r.owner_name}</td>
                      <td className="px-2 py-2 text-soft">{r.city}</td>
                      <td className="px-2 py-2">
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#fffbf0]">{r.status}</span>
                      </td>
                      <td className="px-2 py-2 text-soft">{r.current_step ?? '—'}</td>
                      <td className="px-2 py-2 font-mono text-[11px]">{r.gift_awb || '—'}</td>
                    </tr>
                  ))}
                  {drill.rows.length === 0 && (
                    <tr><td colSpan={6} className="text-center text-soft p-6 italic">No partners in this cell</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
